import { type Store } from 'redux';
import serverVars from 'server-vars';
import { hydrateRoot } from 'react-dom/client';
import { initializeDBL } from 'dibs-buyer-layout/src/entries/index.jsx';
import { sharedLoginCheckSetUp } from '../../authentication/sharedLoginCheck.ts';
import { pdpRespUnavailableLayoutEntry } from '../../pdp/respUnavailable/PdpRespUnavailableLayout.tsx';
import { clientRelayEnvironment } from '../../clientRelayEnvironment.ts';
import { ServerVarsContextProvider } from '../../global/ServerVarsContext/ServerVarsContext.tsx';
import { DblExternalContextProvider } from 'dibs-buyer-layout/exports/DblExternalContext.ts';
import thunk from 'redux-thunk';
import { getIsBotOnClient } from 'dibs-buyer-layout/exports/botHelpers.ts';
import { getIsPdpBottomNewSlModuleTestVariant } from '../../utils/abTestHelper.ts';

/* Reducers */
import { pdpRespNotificationsReducer as sharedNotificationsReducer } from '../../reducers/pdpRespNotificationsReducer.js';
import { defaultReducers } from '../../reducers/reducerUtils.js';

/* Helpers */
import { storePdpVisit } from '../../pdp/PdpSharedNotifications/pdpSharedNotificationHelpers.js';
import { getItemIdFromUri } from '../../utils/getItemIdFromUri.ts';
import { datadog, initSharedDatadogRum, pdpTypes } from 'dibs-datadog/exports/datadog.ts';
import { PAGE_TYPE } from 'dibs-constants/exports/pageTypes.ts';
import { setDatadogABTestValues } from 'dibs-ab-tests/exports/clientAbTestV2.ts';

/* Notifications */
import { pdpRespNotifications } from '../../pdp/PdpRespNotifications/pdpRespNotifications.js';

const isPdpRespPreviewPage = serverVars.get('isPdpRespPreviewPage');

const options = {
    autoRunNotificationQueue: !isPdpRespPreviewPage,
    middleware: [thunk],
    notifications: pdpRespNotifications,
    reducers: {
        sharedNotificationsReducer,
        ...defaultReducers,
    },
    state: serverVars.get('reduxState'),
};

const itemId = getItemIdFromUri(location.pathname);
if (itemId) {
    storePdpVisit({ itemId });
}

datadog.load().then(dd => {
    initSharedDatadogRum(dd, {
        device: 'resp',
        page: PAGE_TYPE.PDP,
        pageSubType: pdpTypes.unavailable,
    });
    setDatadogABTestValues();
});

initializeDBL(options).then(async ({ store }: { store: Store }) => {
    const { environment, socketListeners } = clientRelayEnvironment(
        serverVars.get('dbl.relayData')
    );
    const jsRoot = document.getElementById('js-root');

    sharedLoginCheckSetUp({ store, environment });

    const { Layout } = await pdpRespUnavailableLayoutEntry({
        environment,
        socketListeners,
        relayVars: serverVars.get('relayVars'),
        locale: serverVars.get('locale'),
        messages: serverVars.get('messages'),
    });

    if (!jsRoot) {
        return;
    }

    hydrateRoot(
        jsRoot,
        <ServerVarsContextProvider
            isBot={getIsBotOnClient()}
            isMobile={serverVars.get('settings.isMobile')}
            isTablet={serverVars.get('settings.isTablet')}
            isDesktop={serverVars.get('settings.isDesktop')}
            clientConf={serverVars.get('config')}
            localeOrigin={serverVars.get('localeOrigin')}
            isItemFavoritingFeb2026Variant={serverVars.get('isItemFavoritingFeb2026Variant')}
            isItemFavoritingFeb2026Control={serverVars.get('isItemFavoritingFeb2026Control')}
            isPdpBottomNewSlModuleTestVariant={getIsPdpBottomNewSlModuleTestVariant()}
        >
            <DblExternalContextProvider store={store}>
                <Layout />
            </DblExternalContextProvider>
        </ServerVarsContextProvider>
    );
});
